import type {
  AgenticLogicPublishGateKey,
  AgenticLogicPublishGateStatus,
  AgenticLogicPublishJobStatus,
  AgenticLogicRepairRunStatus,
  AgenticLogicRuleDto,
} from "@intelligems/ig-types";

// ---------------------------------------------------------------------------
// THE one place a rule's lifecycle becomes something a reader sees.
//
// The platform's lifecycle is real and stays real: `draft → active → paused`,
// immutable published versions, a thirteen-gate publish job, a bounded repair
// loop that publishes on the merchant's behalf. None of that is a merchant's
// vocabulary. Without the Advanced toggle a rule has ONE story — it is live —
// plus the two moments that story is not yet true: the deploy is still running,
// or it failed and someone has to act.
//
// Every surface that used to branch on `rule.state` reads this instead. The
// alternative — an `advancedEnabled &&` at each label — puts the same product
// decision in a dozen files and guarantees they drift; the rules list, the rule
// page, the attach picker and the toasts must agree about a rule at all times,
// and the only way to guarantee that is for them to ask the same function.
//
// The derivation is deliberately NOT a lookup on `rule.state` alone. `draft` is
// three different things to a merchant depending on what the publish job and the
// repair run say about it, and `state` cannot tell them apart on its own.
// ---------------------------------------------------------------------------

/**
 * What a reader is told about a rule.
 *
 * The first four kinds are the merchant vocabulary; `draft`/`active`/`paused`
 * pass the platform's own state through untranslated for an advanced org, whose
 * whole point is to see the machine as it is.
 */
export type RuleDisplayStatus =
  /** Serving, or saved and about to be — the ordinary case. */
  | { kind: "live" }
  /** Live, with the deploy still running behind it. Renders a spinner. */
  | { kind: "deploying" }
  /** The deploy failed or the repair loop gave up. Actionable, never silent. */
  | { kind: "error"; failedGate: AgenticLogicPublishGateKey | null }
  /** The repair loop wants a person to accept a behaviour change. */
  | { kind: "needs_approval" }
  /**
   * Paused. Unreachable without the Advanced toggle — pause and resume are not
   * offered — but REACHABLE HISTORICALLY, via the API, or by an Intelligems
   * admin. A paused rule serves nothing, so it must never be drawn as live.
   */
  | { kind: "off" }
  /** Advanced passthrough: the platform's own word. */
  | { kind: "draft" | "active" | "paused" };

/**
 * Copy for a status, so a chip and a picker row cannot disagree.
 *
 * The basic vocabulary is two words wide: a rule is either Ready — it will
 * serve the moment a placement asks for it — or it needs a person. A merchant
 * scanning the list is answering one question, "can I use this?", and the four
 * ways a rule can fail to be ready ("Couldn't deploy", "Needs your approval",
 * "Off") are four answers to a question they have not asked yet. The rule page
 * is where the reason lives; the column only has to route them to it.
 *
 * The advanced kinds keep the platform's own words, which is the whole point of
 * the toggle.
 */
export const RULE_DISPLAY_STATUS_LABEL: Record<
  RuleDisplayStatus["kind"],
  string
> = {
  live: "Ready",
  deploying: "Ready",
  error: "Needs attention",
  needs_approval: "Needs attention",
  off: "Needs attention",
  draft: "Draft",
  active: "Active",
  paused: "Paused",
};

/**
 * The two fields of a publish job this derivation reads.
 *
 * Deliberately structural rather than `AgenticLogicRulePublishJobDto`: the rules
 * list holds the DTO from rule detail while the rule page holds the workspace
 * shell's own `WorkspacePublishJob`, and both must be able to ask this question.
 * Naming either concrete type here would force one caller to convert.
 */
export interface RuleDeployJob {
  status: AgenticLogicPublishJobStatus;
  gates: readonly {
    gateKey: AgenticLogicPublishGateKey;
    status: AgenticLogicPublishGateStatus;
  }[];
}

/** Likewise: the one field of a repair run that changes what a reader is told. */
export interface RuleRepairRun {
  status: AgenticLogicRepairRunStatus;
}

/** Which gate refused, or `null` when the job failed without naming one. */
const failedGateOf = (
  job: RuleDeployJob | null | undefined
): AgenticLogicPublishGateKey | null =>
  job?.gates.find((gate) => gate.status === "failed")?.gateKey ?? null;

/**
 * A repair run that has not settled is a deploy still in flight, even between
 * publish jobs: the loop rewrites the draft and enqueues the NEXT job, so there
 * are windows where `latestPublishJob` has already failed and the rule is not
 * yet in trouble. Reporting an error there would flicker "Couldn't deploy" at a
 * merchant the platform is still working for.
 */
const isRepairInFlight = (repair: RuleRepairRun | null): boolean =>
  repair !== null &&
  (repair.status === "queued" ||
    repair.status === "repairing" ||
    repair.status === "validating");

export interface RuleDisplayStatusInput {
  rule: Pick<AgenticLogicRuleDto, "state">;
  /** The rule's most recent publish job, from `GET /rules/{ruleId}`. */
  publishJob: RuleDeployJob | null | undefined;
  /** The open repair run, when one is attached to the rule. */
  repairRun: RuleRepairRun | null | undefined;
  /** The org's `advanced_agentic_logic_enabled` verdict. */
  advancedEnabled: boolean;
  /**
   * A deploy THIS client just started, before the server has a job to show for
   * it. `publishJob` is the persisted story and lags a click by a mutation plus
   * a poll; in that window it still describes the run that failed, so a reader
   * who just asked for a fix is told the rule is broken and handed no sign that
   * anything is happening. The client knows better, so it may say so.
   */
  deployInFlight?: boolean;
}

/**
 * The rule's status as a reader should be told it.
 *
 * Basic mode collapses `draft` onto the deploy's own outcome, which is the
 * merchant's actual question — "is this working?" — rather than the platform's
 * bookkeeping. `active` is live whatever a stale job says: the version pointer
 * has already moved, and a later failed job is a failed ATTEMPT to replace what
 * is serving, not a rule that stopped serving.
 */
export const ruleDisplayStatus = ({
  rule,
  publishJob,
  repairRun,
  advancedEnabled,
  deployInFlight = false,
}: RuleDisplayStatusInput): RuleDisplayStatus => {
  if (advancedEnabled) return { kind: rule.state };

  if (rule.state === "paused") return { kind: "off" };
  // Ahead of every persisted signal: a run in flight outranks whatever the last
  // job settled on, including a rule that is already serving an older version.
  if (deployInFlight) return { kind: "deploying" };
  if (rule.state === "active") return { kind: "live" };

  // `draft` — the only state whose meaning depends on the deploy.
  if (repairRun?.status === "awaiting_approval")
    return { kind: "needs_approval" };
  if (isRepairInFlight(repairRun ?? null)) return { kind: "deploying" };
  if (repairRun?.status === "gave_up") {
    return { kind: "error", failedGate: failedGateOf(publishJob) };
  }

  if (publishJob?.status === "queued" || publishJob?.status === "running") {
    return { kind: "deploying" };
  }
  if (publishJob?.status === "failed") {
    return { kind: "error", failedGate: failedGateOf(publishJob) };
  }
  // Stopped by hand. Nothing failed, but nothing is working on the draft
  // either and it is not live: that needs a person, same as a failed run —
  // never a spinner that has nothing to wait for.
  if (publishJob?.status === "cancelled") {
    return { kind: "error", failedGate: null };
  }

  // No job yet. Atlas enqueues one on the first draft save, so this is the gap
  // between the rule row existing and its deploy starting — still in progress
  // from the merchant's side, never an error.
  return { kind: "deploying" };
};

/** Whether the rule can serve right now. Drives the attach picker. */
export const isRuleAttachable = (status: RuleDisplayStatus): boolean =>
  status.kind === "live" || status.kind === "active";

/** Why the picker is refusing this rule, or `null` when it is not. */
export const ruleAttachBlockedReason = (
  status: RuleDisplayStatus
): string | null => {
  switch (status.kind) {
    case "live":
    case "active":
      return null;
    case "deploying":
      return "Still deploying — it can be attached once it finishes.";
    case "error":
      return "This rule couldn't deploy. Open it to see what to fix.";
    case "needs_approval":
      return "This rule is waiting for your approval. Open it to review the change.";
    case "off":
    case "paused":
      return "This rule is off.";
    case "draft":
      return "This rule has no published version yet.";
  }
};
